<template>
  <div class="running-workflows-page">
    <div class="page-header">
      <div class="header-left">
        <h1>Running Workflows</h1>
        <router-link to="/admin/scout/workflow/history" class="nav-link">
          <i class="bi bi-clock-history"></i>
          View Execution History
        </router-link>
      </div>
      <div class="header-actions">
        <div class="refresh-control">
          <label>Auto-refresh:</label>
          <select v-model.number="refreshInterval" class="refresh-select">
            <option :value="0">Off</option>
            <option :value="5">5s</option>
            <option :value="10">10s</option>
            <option :value="15">15s</option>
            <option :value="30">30s</option>
            <option :value="60">1m</option>
          </select>
          <span v-if="refreshInterval > 0 && !loading" class="refresh-countdown">
            Next refresh in {{ countdown }}s
          </span>
        </div>
        <button @click="loadRunningWorkflows" class="btn-primary" :disabled="loading">
          <span v-if="loading">↻</span>
          <span v-else>Refresh Now</span>
        </button>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="summary-cards">
      <div class="summary-card">
        <div class="card-icon">
          <i class="bi bi-play-circle-fill"></i>
        </div>
        <div class="card-details">
          <div class="card-label">Currently Running</div>
          <div class="card-value">{{ runningWorkflows.length }}</div>
        </div>
      </div>
      
      <div class="summary-card paused">
        <div class="card-icon">
          <i class="bi bi-pause-circle-fill"></i>
        </div>
        <div class="card-details">
          <div class="card-label">Paused</div>
          <div class="card-value">{{ pausedCount }}</div>
        </div>
      </div>
      
      <div class="summary-card">
        <div class="card-icon">
          <i class="bi bi-stopwatch-fill"></i>
        </div>
        <div class="card-details">
          <div class="card-label">Longest Running</div>
          <div class="card-value">{{ longestDuration }}</div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-section">
      <div class="filter-group">
        <label for="status-filter">Status:</label>
        <select 
          id="status-filter" 
          v-model="statusFilter" 
          @change="handleFilterChange"
          class="filter-select"
        >
          <option value="">All</option>
          <option value="running">Running</option>
          <option value="paused">Paused</option>
        </select>
      </div>

      <div class="filter-group">
        <label for="type-filter">Type:</label>
        <select 
          id="type-filter" 
          v-model="typeFilter" 
          @change="handleFilterChange"
          class="filter-select"
        >
          <option value="">All</option>
          <option value="project">Project</option>
          <option value="component">Component</option>
        </select>
      </div>

      <div class="filter-group">
        <label for="workflow-name-filter">Workflow Name:</label>
        <input 
          id="workflow-name-filter"
          type="text"
          v-model="workflowNameFilter"
          @input="handleFilterChange"
          placeholder="Search by name..."
          class="filter-input"
        />
      </div>

      <div class="filter-group">
        <label for="ref-filter">Ref:</label>
        <input 
          id="ref-filter"
          type="text"
          v-model="refFilter"
          @input="handleFilterChange"
          placeholder="Search by ref..."
          class="filter-input"
        />
      </div>

      <div class="filter-group">
        <label for="context-filter">Context UUID:</label>
        <input 
          id="context-filter"
          type="text"
          v-model="contextFilter"
          @input="handleFilterChange"
          placeholder="Search by context UUID..."
          class="filter-input"
        />
      </div>

      <div class="filter-group">
        <label for="page-size">Per Page:</label>
        <select 
          id="page-size" 
          v-model.number="pageSize" 
          @change="handlePageSizeChange"
          class="filter-select"
        >
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </div>
    </div>

    <div v-if="error" class="error-message">
      <span>⚠️ {{ error }}</span>
      <button @click="loadRunningWorkflows" class="btn-text">Try Again</button>
    </div>

    <div v-if="loading && !runningWorkflows.length" class="loading-state">
      <div class="spinner"></div>
      <p>Loading running workflows...</p>
    </div>

    <div v-else-if="!loading && !runningWorkflows.length" class="empty-state">
      <div class="empty-icon">
        <i class="bi bi-pause-circle"></i>
      </div>
      <h3>No running workflows</h3>
      <p>There are currently no active workflow executions.</p>
    </div>

    <div v-else class="workflows-grid">
      <template v-for="group in groupedWorkflows">
        <!-- Context Group Header -->
        <div :key="`context-${group.contextId}`" class="context-group-header">
          <div class="context-header-content">
            <i class="bi bi-folder2-open"></i>
            <span v-if="group.contextId !== 'no-context'" class="context-label">
              Context: <code>{{ truncateUuid(group.contextId, 16) }}</code>
            </span>
            <span v-else class="context-label">No Context</span>
            <CopyButton v-if="group.contextId !== 'no-context'" :text="group.contextId" />
            <span class="execution-count">{{ group.executions.length }} execution{{ group.executions.length > 1 ? 's' : '' }}</span>
          </div>
        </div>
        
        <!-- Workflow cards in this context -->
        <div 
          v-for="execution in group.executions" 
          :key="execution.execution_uuid"
          class="workflow-card"
          @click="viewDetails(execution.execution_uuid)"
        >
        <div class="card-header">
          <div class="workflow-info">
            <h3>{{ execution.workflow_name }}</h3>
            <span class="type-badge" :class="execution.type">
              {{ execution.type }}
            </span>
          </div>
          <StatusBadge :status="execution.status" />
        </div>

        <div class="card-body">
          <div class="info-row">
            <span class="label">Execution ID:</span>
            <div class="value">
              <code>{{ truncateUuid(execution.execution_uuid, 12) }}</code>
              <CopyButton :text="execution.execution_uuid" />
            </div>
          </div>

          <div class="info-row" v-if="execution.context_uuid">
            <span class="label">Context ID:</span>
            <div class="value">
              <code>{{ truncateUuid(execution.context_uuid, 12) }}</code>
              <CopyButton :text="execution.context_uuid" />
            </div>
          </div>

          <div class="info-row" v-if="execution.ref">
            <span class="label">Ref:</span>
            <code class="value ref-code">{{ truncateUuid(execution.ref, 16) }}</code>
          </div>

          <div class="info-row">
            <span class="label">Environment:</span>
            <span class="env-badge" :class="execution.env">
              {{ execution.env.toUpperCase() }}
            </span>
          </div>

          <div class="info-row">
            <span class="label">Started:</span>
            <span class="value">{{ formatRelativeTime(execution.start_time) }}</span>
          </div>

          <div class="info-row">
            <span class="label">Running for:</span>
            <span class="value duration">{{ formatDurationShort(calculateRunningDuration(execution.start_time)) }}</span>
          </div>

          <div v-if="execution.current_node_id" class="info-row">
            <span class="label">Current Node:</span>
            <span class="value">{{ getCurrentNodeName(execution) }}</span>
          </div>

          <!-- Progress indicator -->
          <div class="progress-section">
            <div class="progress-label">
              <span>Progress</span>
              <span>{{ getCompletedNodesCount(execution) }} / {{ execution.execution_nodes ? execution.execution_nodes.length : 0 }} nodes</span>
            </div>
            <div class="progress-bar">
              <div 
                class="progress-fill"
                :style="{ width: getProgressPercentage(execution) + '%' }"
              ></div>
            </div>
          </div>
        </div>

        <div class="card-footer">
          <button 
            @click.stop="viewDetails(execution.execution_uuid)" 
            class="btn-view"
          >
            View Details <i class="bi bi-arrow-right"></i>
          </button>
          
          <div class="control-buttons">
            <button
              @click.stop="openQuickPeek(execution.execution_uuid)"
              class="btn-control peek"
              title="Quick Peek"
            >
              <i class="bi bi-eye"></i>
            </button>
            <button 
              v-if="execution.status === 'running'"
              @click.stop="confirmPauseWorkflow(execution.execution_uuid, execution.workflow_name)"
              class="btn-control pause"
              title="Pause Workflow"
            >
              <i class="bi bi-pause-circle"></i>
            </button>
            <button 
              v-if="execution.status === 'paused'"
              @click.stop="resumeWorkflow(execution.execution_uuid)"
              class="btn-control resume"
              title="Resume Workflow"
            >
              <i class="bi bi-play-circle"></i>
            </button>
            <button 
              @click.stop="confirmKillWorkflow(execution.execution_uuid, execution.workflow_name)"
              class="btn-control kill"
              title="Kill Workflow"
            >
              <i class="bi bi-x-circle"></i>
            </button>
          </div>
        </div>
      </div>
      </template>
    </div>

    <!-- Pagination -->
    <div v-if="runningWorkflows.length > 0" class="pagination">
      <button 
        @click="prevPage" 
        :disabled="currentPage === 0 || loading"
        class="btn-pagination"
      >
        ← Previous
      </button>
      <span class="pagination-info">
        Page {{ currentPage + 1 }}<span v-if="totalPages > 0"> of {{ totalPages }}</span>
        <span class="total-records">({{ totalRecords }} total)</span>
      </span>
      <button 
        @click="nextPage" 
        :disabled="!hasMore || loading"
        class="btn-pagination"
      >
        Next →
      </button>
    </div>

    <!-- Quick Peek Modal -->
    <WorkflowQuickPeekModal
      :show="quickPeekModal.show"
      :executionUuid="quickPeekModal.executionUuid"
      :executionData="quickPeekModal.executionData"
      :workflowHistoryService="workflowHistoryService"
      mode="running"
      @close="closeQuickPeek"
    />

    <!-- Confirmation Modal -->
    <ConfirmModal
      :show="confirmModal.show"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :confirm-text="confirmModal.confirmText"
      :danger-button="confirmModal.danger"
      :icon="confirmModal.icon"
      @confirm="handleConfirmAction"
      @cancel="closeConfirmModal"
    />
  </div>
</template>

<script>
import WorkflowHistory from '@/lib/WorkflowHistory';
import StatusBadge from '@/components/workflow/StatusBadge.vue';
import CopyButton from '@/components/workflow/CopyButton.vue';
import ConfirmModal from '@/components/workflow/ConfirmModal.vue';
import WorkflowQuickPeekModal from '@/components/workflow/WorkflowQuickPeekModal.vue';
import { 
  formatDurationShort, 
  formatRelativeTime, 
  truncateUuid
} from '@/shared/workflowHistoryUtils';

export default {
  name: 'RunningWorkflows',
  components: {
    StatusBadge,
    CopyButton,
    ConfirmModal,
    WorkflowQuickPeekModal
  },
  data() {
    return {
      runningWorkflows: [],
      totalRecords: 0,
      currentPage: 0,
      pageSize: 20,
      hasMore: true,
      loading: false,
      error: null,
      statusFilter: '',
      typeFilter: '',
      workflowNameFilter: '',
      refFilter: '',
      contextFilter: '',
      workflowHistoryService: null,
      refreshInterval: 10, // seconds
      refreshTimer: null,
      countdown: 0,
      countdownTimer: null,
      filterDebounceTimer: null,
      quickPeekModal: {
        show: false,
        executionUuid: null,
        executionData: null
      },
      confirmModal: {
        show: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        danger: false,
        icon: 'warning',
        action: null,
        params: null
      }
    };
  },
  computed: {
    groupedWorkflows() {
      // Client-side grouping from flat array by context_uuid
      const groups = {};
      this.runningWorkflows.forEach(execution => {
        const key = execution.context_uuid || 'no-context';
        if (!groups[key]) {
          groups[key] = { contextId: key, executions: [] };
        }
        groups[key].executions.push(execution);
      });
      // Sort executions within each group by start_time descending
      return Object.values(groups).map(group => ({
        ...group,
        executions: group.executions.slice().sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
      }));
    },
    pausedCount() {
      return this.runningWorkflows.filter(w => w.status === 'paused').length;
    },
    longestDuration() {
      if (this.runningWorkflows.length === 0) return '0s';
      const longest = Math.max(
        ...this.runningWorkflows.map(w => this.calculateRunningDuration(w.start_time))
      );
      return formatDurationShort(longest);
    },
    totalPages() {
      return Math.ceil(this.totalRecords / this.pageSize);
    }
  },
  mounted() {
    this.workflowHistoryService = new WorkflowHistory(this.$workflowApi);
    this.restoreFromUrl();
    this.loadRunningWorkflows();
    this.startAutoRefresh();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
    this.stopCountdown();
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
    }
  },
  watch: {
    refreshInterval(newVal) {
      this.stopAutoRefresh();
      if (newVal > 0) {
        this.startAutoRefresh();
      } else {
        this.stopCountdown();
      }
    }
  },
  methods: {
    formatDurationShort,
    formatRelativeTime,
    truncateUuid,

    async loadRunningWorkflows() {
      this.loading = true;
      this.error = null;
      
      try {
        const params = {
          limit: this.pageSize,
          offset: this.currentPage * this.pageSize
        };
        
        if (this.statusFilter) {
          params.status = this.statusFilter;
        }
        
        if (this.typeFilter) {
          params.type = this.typeFilter;
        }
        
        if (this.workflowNameFilter) {
          params.workflow_name = this.workflowNameFilter;
        }
        
        if (this.refFilter) {
          params.ref = this.refFilter;
        }

        if (this.contextFilter) {
          params.context_uuid = this.contextFilter;
        }
        
        const result = await this.workflowHistoryService.fetchRunningWorkflows(params);
        this.runningWorkflows = result.data;
        this.totalRecords = result.total;
        this.hasMore = (this.currentPage + 1) * this.pageSize < result.total;
        this.updateUrl();
        
        // Reset countdown when data is refreshed
        if (this.refreshInterval > 0) {
          this.countdown = this.refreshInterval;
        }
      } catch (err) {
        this.error = err.message || 'Failed to load running workflows';
        console.error('Error loading running workflows:', err);
      } finally {
        this.loading = false;
      }
    },

    handleFilterChange() {
      // Debounce text input filters
      if (this.filterDebounceTimer) {
        clearTimeout(this.filterDebounceTimer);
      }
      
      this.filterDebounceTimer = setTimeout(() => {
        this.currentPage = 0;
        this.updateUrl();
        this.loadRunningWorkflows();
      }, 500);
    },

    handlePageSizeChange() {
      this.currentPage = 0;
      this.updateUrl();
      this.loadRunningWorkflows();
    },

    nextPage() {
      if (this.hasMore && !this.loading) {
        this.currentPage++;
        this.updateUrl();
        this.loadRunningWorkflows();
      }
    },

    prevPage() {
      if (this.currentPage > 0 && !this.loading) {
        this.currentPage--;
        this.updateUrl();
        this.loadRunningWorkflows();
      }
    },

    startAutoRefresh() {
      if (this.refreshInterval > 0) {
        this.countdown = this.refreshInterval;
        this.startCountdown();
        
        this.refreshTimer = setInterval(() => {
          this.loadRunningWorkflows();
        }, this.refreshInterval * 1000);
      }
    },

    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },

    startCountdown() {
      this.stopCountdown();
      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          this.countdown = this.refreshInterval;
        }
      }, 1000);
    },

    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },

    calculateRunningDuration(startTime) {
      const start = new Date(startTime);
      const now = new Date();
      return now.getTime() - start.getTime();
    },

    getCurrentNodeName(execution) {
      if (!execution.current_node_id) return 'N/A';
      if (!execution.execution_nodes) return 'N/A';
      const nodeItem = execution.execution_nodes.find(n => n.node.id === execution.current_node_id);
      return nodeItem ? nodeItem.node.name : `Node ${execution.current_node_id}`;
    },

    getCompletedNodesCount(execution) {
      if (!execution.execution_nodes) return 0;
      return execution.execution_nodes.filter(nodeItem => {
        const state = nodeItem.node.State || nodeItem.node.state;
        return state && state.status === 'completed';
      }).length;
    },

    getProgressPercentage(execution) {
      if (!execution.execution_nodes || execution.execution_nodes.length === 0) return 0;
      return Math.round((this.getCompletedNodesCount(execution) / execution.execution_nodes.length) * 100);
    },

    viewDetails(executionUuid) {
      this.$router.push(`/admin/scout/workflow/running/${executionUuid}`);
    },

    openQuickPeek(executionUuid) {
      const execution = this.runningWorkflows.find(w => w.execution_uuid === executionUuid);
      this.quickPeekModal = {
        show: true,
        executionUuid,
        executionData: execution || null
      };
    },

    closeQuickPeek() {
      this.quickPeekModal = {
        show: false,
        executionUuid: null,
        executionData: null
      };
    },

    restoreFromUrl() {
      const query = this.$route.query;
      if (query.page !== undefined) {
        const page = parseInt(query.page, 10);
        if (!isNaN(page) && page >= 0) this.currentPage = page;
      }
      if (query.pageSize !== undefined) {
        const size = parseInt(query.pageSize, 10);
        if ([10, 20, 50, 100].includes(size)) this.pageSize = size;
      }
      if (query.status) this.statusFilter = query.status;
      if (query.type) this.typeFilter = query.type;
      if (query.workflow_name) this.workflowNameFilter = query.workflow_name;
      if (query.ref) this.refFilter = query.ref;
      if (query.context_uuid) this.contextFilter = query.context_uuid;
    },

    updateUrl() {
      const query = {};
      if (this.currentPage > 0) query.page = String(this.currentPage);
      if (this.pageSize !== 20) query.pageSize = String(this.pageSize);
      if (this.statusFilter) query.status = this.statusFilter;
      if (this.typeFilter) query.type = this.typeFilter;
      if (this.workflowNameFilter) query.workflow_name = this.workflowNameFilter;
      if (this.refFilter) query.ref = this.refFilter;
      if (this.contextFilter) query.context_uuid = this.contextFilter;

      const currentQuery = this.$route.query;
      const changed = Object.keys(query).length !== Object.keys(currentQuery).length ||
        Object.keys(query).some(k => query[k] !== currentQuery[k]);
      if (changed) {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    confirmPauseWorkflow(executionUuid, workflowName) {
      this.confirmModal = {
        show: true,
        title: 'Pause Workflow',
        message: `Are you sure you want to pause the workflow "${workflowName}"? This will pause execution until you resume it.`,
        confirmText: 'Pause Workflow',
        danger: false,
        icon: 'warning',
        action: 'pauseWorkflow',
        params: { executionUuid }
      };
    },

    async pauseWorkflow(executionUuid) {
      try {
        await this.workflowHistoryService.pauseWorkflow(executionUuid);
        const wf = this.runningWorkflows.find(w => w.execution_uuid === executionUuid);
        if (wf) {
          wf.status = 'paused';
        }
        this.$bvToast.toast('Workflow paused successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to pause workflow', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error pausing workflow:', error);
      }
    },

    async resumeWorkflow(executionUuid) {
      try {
        await this.workflowHistoryService.resumeWorkflow(executionUuid);
        const wf = this.runningWorkflows.find(w => w.execution_uuid === executionUuid);
        if (wf) {
          wf.status = 'running';
        }
        this.$bvToast.toast('Workflow resumed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to resume workflow', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error resuming workflow:', error);
      }
    },

    confirmKillWorkflow(executionUuid, workflowName) {
      this.confirmModal = {
        show: true,
        title: 'Kill Workflow',
        message: `Are you sure you want to kill the workflow "${workflowName}"? This action cannot be undone.`,
        confirmText: 'Kill Workflow',
        danger: true,
        icon: 'danger',
        action: 'killWorkflow',
        params: { executionUuid }
      };
    },

    async killWorkflow(executionUuid) {
      try {
        await this.workflowHistoryService.killWorkflow(executionUuid);
        this.$bvToast.toast('Workflow killed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
        // Remove from flat array immediately
        this.runningWorkflows = this.runningWorkflows.filter(w => w.execution_uuid !== executionUuid);
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to kill workflow', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error killing workflow:', error);
      }
    },

    handleConfirmAction() {
      const { action, params } = this.confirmModal;
      if (action === 'killWorkflow') {
        this.killWorkflow(params.executionUuid);
      } else if (action === 'pauseWorkflow') {
        this.pauseWorkflow(params.executionUuid);
      }
      this.closeConfirmModal();
    },

    closeConfirmModal() {
      this.confirmModal = {
        show: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        danger: false,
        icon: 'warning',
        action: null,
        params: null
      };
    }
  }
};
</script>

<style scoped>
.running-workflows-page {
  padding: 24px;
  background-color: var(--bg-card);
  min-height: 100vh;
  color: var(--text-primary);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.header-left h1 {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  color: var(--text-primary);
}

.nav-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: var(--form-bg);
  border: 1px solid var(--border-default);
  border-radius: 6px;
  color: var(--primary-start);
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
}

.nav-link:hover {
  background-color: var(--bg-card);
  border-color: var(--primary-start);
}

.page-header h1 {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  color: var(--text-primary);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.refresh-control {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: var(--form-bg);
  border: 1px solid var(--border-default);
  border-radius: 6px;
}

.refresh-control label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.refresh-select {
  padding: 6px 10px;
  border: 1px solid var(--border-default);
  border-radius: 4px;
  background-color: var(--bg-card);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
}

.refresh-countdown {
  font-size: 13px;
  color: #9ca3af;
  font-style: italic;
}

.btn-primary {
  padding: 10px 20px;
  background-color: var(--primary-start);
  color: var(--text-primary);
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
  background-color: hsl(243, 91%, 64%);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.summary-card {
  background-color: var(--form-bg);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.summary-card.paused {
  border-color: #f59e0b;
}

.card-icon {
  font-size: 32px;
}

.card-details {
  flex: 1;
}

.card-label {
  font-size: 14px;
  color: #9ca3af;
  margin-bottom: 4px;
}

.card-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
}

.filters-section {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  padding: 16px;
  background-color: var(--form-bg);
  border-radius: 8px;
  border: 1px solid var(--border-default);
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-group label {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
  white-space: nowrap;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid var(--border-default);
  border-radius: 6px;
  background-color: var(--bg-card);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  min-width: 120px;
}

.filter-input {
  padding: 8px 12px;
  border: 1px solid var(--border-default);
  border-radius: 6px;
  background-color: var(--bg-card);
  color: var(--text-primary);
  font-size: 14px;
  min-width: 200px;
}

.filter-input::placeholder {
  color: #6b7280;
}

.filter-input:focus,
.filter-select:focus {
  outline: none;
  border-color: var(--primary-start);
}

.error-message {
  background-color: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #ef4444;
}

.btn-text {
  background: transparent;
  border: none;
  color: var(--primary-start);
  cursor: pointer;
  font-weight: 600;
  text-decoration: underline;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-default);
  border-top-color: var(--primary-start);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 20px;
  margin-bottom: 8px;
  color: var(--text-primary);
}

.empty-state p {
  color: #9ca3af;
}

.workflows-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
}

.workflows-grid .context-group-header {
  grid-column: 1 / -1;
  background-color: var(--bg-card);
  border: 1px solid var(--border-default);
  border-left: 4px solid var(--primary-start);
  border-radius: 8px;
  padding: 16px 20px;
  margin-top: 12px;
}

.workflows-grid .context-group-header:first-child {
  margin-top: 0;
}

.workflows-grid .context-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-primary);
}

.workflows-grid .context-header-content i {
  font-size: 18px;
  color: var(--primary-start);
}

.workflows-grid .context-label {
  font-size: 15px;
  font-weight: 600;
}

.workflows-grid .context-label code {
  background-color: var(--form-bg);
  padding: 4px 8px;
  border-radius: 4px;
  color: var(--primary-start);
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 12px;
  margin-left: 4px;
}

.workflows-grid .execution-count {
  margin-left: auto;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 400;
}

.workflow-card {
  background-color: var(--form-bg);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.workflow-card:hover {
  border-color: var(--primary-start);
  box-shadow: 0 4px 12px rgba(108, 99, 255, 0.2);
}

.card-header {
  padding: 16px;
  background-color: var(--bg-card);
  border-bottom: 1px solid var(--border-default);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.workflow-info h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: var(--text-primary);
}

.type-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
}

.type-badge.project {
  background-color: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.type-badge.component {
  background-color: rgba(139, 92, 246, 0.2);
  color: #a78bfa;
  border: 1px solid rgba(139, 92, 246, 0.3);
}

.card-body {
  padding: 16px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
}

.info-row .label {
  color: #9ca3af;
  font-weight: 500;
}

.info-row .value {
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-row code {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.85rem;
  color: #a0aec0;
}

.ref-code {
  background-color: rgba(108, 99, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.env-badge {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.env-badge.dev {
  background-color: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
}

.env-badge.stage {
  background-color: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
}

.env-badge.live {
  background-color: rgba(16, 185, 129, 0.2);
  color: #34d399;
}

.duration {
  font-weight: 600;
  color: var(--primary-start) !important;
}

.progress-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border-default);
}

.progress-label {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
  color: #9ca3af;
}

.progress-bar {
  height: 8px;
  background-color: var(--bg-card);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--primary-start), #8b5cf6);
  transition: width 0.3s ease;
}

.card-footer {
  padding: 12px 16px;
  background-color: var(--bg-card);
  border-top: 1px solid var(--border-default);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.control-buttons {
  display: flex;
  gap: 8px;
}

.btn-view {
  background: transparent;
  border: 1px solid var(--primary-start);
  color: var(--primary-start);
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
}

.btn-view:hover {
  background-color: var(--primary-start);
  color: var(--text-primary);
}

.btn-control {
  background: transparent;
  border: 1px solid #6b7280;
  color: #9ca3af;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-control:hover {
  background-color: rgba(107, 114, 128, 0.1);
  border-color: #9ca3af;
  color: var(--text-primary);
}

.btn-control.pause:hover {
  border-color: #f59e0b;
  color: #f59e0b;
  background-color: rgba(245, 158, 11, 0.1);
}

.btn-control.resume:hover {
  border-color: #10b981;
  color: #10b981;
  background-color: rgba(16, 185, 129, 0.1);
}

.btn-control.kill:hover {
  border-color: #ef4444;
  color: #ef4444;
  background-color: rgba(239, 68, 68, 0.1);
}

.btn-control.peek {
  border-color: var(--primary-start);
  color: var(--primary-start);
}

.btn-control.peek:hover {
  background-color: rgba(108, 99, 255, 0.1);
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding: 16px;
}

.btn-pagination {
  padding: 8px 16px;
  background-color: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--text-primary);
}

.btn-pagination:hover:not(:disabled) {
  background-color: var(--bg-card);
  border-color: var(--border-hover);
}

.btn-pagination:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  color: #9ca3af;
  font-weight: 600;
}

.total-records {
  margin-left: 4px;
  font-weight: 400;
  color: #6b7280;
}
</style>
